"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { getFormattedDate } from "@/utils/utils";

interface NewsCardProps {
  image: string;
  title: string;
  category?: string;
  date?: string;
  excerpt: string;
  alt?: string;
  href: string;
  lang?: string;
  variant?: "default" | "detailed";
  type?: "NEWS" | "PROJECTS";
}

export default function NewsCard({
  image,
  title,
  category,
  date,
  excerpt,
  alt,
  href,
  lang = "en",
  variant = "default",
  type = "NEWS",
}: NewsCardProps) {
  const isDetailed = variant === "detailed";
  return (
    <Link href={href}>
      <motion.div
        className="bg-white rounded-[10px] border border-[#f4f5f6] shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] overflow-hidden group cursor-pointer transition-all duration-300"
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3 }}
      >
        {/* Image Container */}
        <div className="relative w-full h-[200px] sm:h-[240px] lg:h-[260px] overflow-hidden border-b border-[#f4f5f6]">
          {image && (
            <Image
              src={image || ""}
              alt={alt || title}
              fill
              draggable={false}
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            />
          )}
          {/* Date Badge */}
          {date && (
            <div className="absolute top-2 right-2 sm:top-[10px] sm:right-[10px] bg-[#1a5852] text-white px-2 py-2 sm:px-3 sm:py-3 rounded-[6px] text-sm sm:text-base font-normal leading-[17.6px]">
              {getFormattedDate(date)}
            </div>
          )}
        </div>

        {/* Content Container */}
        <div className="p-4 sm:p-6 lg:p-7">
          <div className="space-y-[18px]">
            <div className="space-y-[6px]">
              {type === "NEWS" && (
                <span className="text-[#4f5662] text-sm sm:text-base font-normal leading-[17.6px] capitalize">
                  {category || (
                    <span className="hidden md:block md:h-6">&nbsp;</span>
                  )}
                </span>
              )}
              <h3
                className={`text-[#1a1d21] text-lg sm:text-xl lg:text-2xl font-medium leading-[28px] sm:leading-[32px] lg:leading-[34px] tracking-[-0.72px] transition-colors duration-300 group-hover:text-[#1a5852] ${
                  isDetailed
                    ? "min-h-[84px] sm:min-h-[96px] lg:min-h-[102px]" // 3 lines with responsive min height
                    : "md:min-h-[68px]" // 2 lines with responsive min height
                }`}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: isDetailed ? 3 : 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </h3>
            </div>

            <div className="w-full h-[2px] bg-[#f4f5f6] relative overflow-hidden">
              <div className="absolute left-0 top-0 w-0 h-full bg-[#1a5852] transition-all duration-500 ease-out group-hover:w-full"></div>
            </div>

            <p
              className={`text-[#4f5662] text-sm sm:text-base font-normal leading-[22px] sm:leading-[26px] ${
                isDetailed
                  ? "min-h-[66px] sm:min-h-[78px]" // 3 lines with responsive min height
                  : "min-h-[44px] sm:min-h-[52px]" // 2 lines with responsive min height
              }`}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: isDetailed ? 3 : 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {excerpt}
            </p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

// Alternative compact version for use in sliders or smaller contexts
export function NewsCardCompact({
  image,
  title,
  category,
  date,
  excerpt,
  alt,
  href,
  lang = "en",
}: NewsCardProps) {
  return (
    <Link href={href}>
      <motion.div
        className="bg-white rounded-[8px] border border-[#f4f5f6] shadow-lg overflow-hidden group cursor-pointer transition-all duration-300"
        whileHover={{ y: -4, scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        {/* Image Container */}
        <div className="relative w-full h-[180px] overflow-hidden">
          {image && (
            <Image
              src={image || ""}
              alt={alt || title}
              fill
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            />
          )}
          {/* Date Badge */}
          <div className="absolute top-2 right-2 bg-[#1a5852] text-white px-2 py-1 rounded text-sm">
            {date.split(" ").slice(0, 2).join(" ")}
          </div>
        </div>

        {/* Content Container */}
        <div className="p-4">
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-[#4f5662] text-sm font-normal capitalize">
                {category}
              </span>
              <h3
                className="text-[#1a1d21] text-lg font-medium transition-colors duration-300 group-hover:text-[#1a5852]"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </h3>
            </div>

            <div className="w-full h-[1px] bg-[#f4f5f6] relative overflow-hidden">
              <div className="absolute left-0 top-0 w-0 h-full bg-[#1a5852] transition-all duration-500 ease-out group-hover:w-full"></div>
            </div>

            <p
              className="text-[#4f5662] text-sm font-normal leading-relaxed"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {excerpt}
            </p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
